import axiosInstance from "../variables/api/axiosInstance";
import axiosMultiPartDataInstance from "../variables/api/axiosMultiPartDatainstance";
import axiosMultiPartDatainstance from "../variables/api/axiosMultiPartDatainstance";
import { API_URL, LOGGED_USER } from "../variables/constants";
import { toast } from "react-toastify";
import { searchActiveProductionRefined, searchProduction } from "./useSearch";

// Cache des raisons sociales des compagnies membres (type_organisme = ASSURANCE). Un
// organisme de type POOL (ex. « POOL TPVM-VT GIE » pour les VT) n'y figure pas.
let _compagniesRaisonSocialesCache = null;
const normaliserRaisonSociale = (v) => (v || "").trim().toUpperCase();
const recupererRaisonSocialesCompagnies = async () => {
  if (_compagniesRaisonSocialesCache) return _compagniesRaisonSocialesCache;
  try {
    const res = await axiosInstance.get(`/api/organisme/assurance`);
    _compagniesRaisonSocialesCache = new Set(
      (Array.isArray(res.data) ? res.data : [])
        .map((o) => normaliserRaisonSociale(o?.raisonSociale))
        .filter(Boolean)
    );
  } catch {
    _compagniesRaisonSocialesCache = new Set();
  }
  return _compagniesRaisonSocialesCache;
};

export const recupererLesClients = () => {
  return axiosInstance.get(`/api/personne/client`);
};

export const recupererLesContrats = async (search) => {
  return await searchProduction(search);
};

// Déclaration de sinistre : recherche affinée (même moteur que la navbar /production).
// - COMPAGNIE connectée : portée siège (/api/contrat/search filtre déjà sur sa compagnie).
// - POOL : voit TOUS les contrats de type Pool. Les VT appartiennent à un organisme Pool
//   DISTINCT (« POOL TPVM-VT GIE »), de code différent → on ne peut pas restreindre par le
//   seul code de l'utilisateur (cela les exclurait). On récupère donc large et on exclut
//   côté front uniquement les contrats appartenant à une COMPAGNIE membre.
export const recupererLesContratsActifs = async (search) => {
  const isPool = LOGGED_USER?.profil?.organisme?.typeOrganisme === "POOL";
  if (!isPool) {
    return await searchActiveProductionRefined(search, 30);
  }
  const [response, compagnies] = await Promise.all([
    searchActiveProductionRefined(search, 60),
    recupererRaisonSocialesCompagnies(),
  ]);
  const content = (response?.data?.content || []).filter(
    (c) => !compagnies.has(normaliserRaisonSociale(c?.raisonSociale))
  );
  return { ...response, data: { ...response.data, content } };
};

export const recupererLesEntitesDeconstat = () => {
  return axiosInstance.get(`/api/entite-constat`);
};

export const recupererLeContratDunClient = (numeroAssure) => {
  return axiosInstance.get(`/api/contrat/souscripteur/${numeroAssure}`);
};

export const ajouterUnTemoin = (temoin) => {
  return axiosInstance.post(`/api/personne`, temoin);
};

export const addPersonne = (personne) => {
  return axiosInstance.post(`/api/personne`, personne);
};

export const updatePersonne = (personne) => {
  return axiosInstance.put(`/api/personne`, personne);
};

export const ajouterUneVictime = (victime) => {
  return axiosInstance.post(`/api/victime`, victime);
};

export const editerUneVictime = (victime) => {
  return axiosInstance.put(`/api/victime`, victime);
};

export const ajouterUnEngin = (engin) => {
  return axiosInstance.post(`/api/engin`, engin);
};

export const ajouterSinistre = (sinistre) => {
  return axiosInstance.post(`/api/sinistre/sinister`, sinistre);
};

export const getSinistreById = (codeId) => {
  return axiosInstance.get(`/api/sinistre/codeId/${codeId}`);
};

export const getParametre = (typeParametre) => {
  return axiosInstance.get(`/api/parametre/type/${typeParametre}`);
};

export const getAllEvaluations = () => {
  return axiosInstance.get(`/api/evaluation/`);
};

export const getAllEvaluationsWithSinistre = () => {
  return axiosInstance.get(`/api/evaluation/sinistre`);
};

export const getAllDecaissementWithSinistre = () => {
  return axiosInstance.get(`/api/decaissement/sinistre`);
};

export const saveSinistreDeclaration = (sinistreDTO) => {
  return axiosInstance.post(`/api/sinistre/web`, sinistreDTO);
};

export const saveReevaluation = (evaluation) => {
  return axiosInstance.post(`/api/evaluation/reevaluation`, evaluation);
};

export const updateVictime = (victime) => {
  return axiosInstance.put(`/api/victime/ind`, victime);
};

export const updateAyantDroit = (ayantDroit) => {
  return axiosInstance.put(`/api/victime/ayant-droit`, ayantDroit);
};

export const updateRepresentant = (representant) => {
  return axiosInstance.put(`/api/victime/representant`, representant);
};

export const saveVictime = (victimeDTO) => {
  return axiosInstance.post(`/api/victime/ind`, victimeDTO);
};

export const saveAdversaire = (adversaireDTO) => {
  return axiosInstance.post(`/api/victime/adversaires`, adversaireDTO);
};

export const saveAyantDroit = (ayantDroitDTO) => {
  return axiosInstance.post(`/api/victime/ayant-droit`, ayantDroitDTO);
};

export const saveRepresentant = (representantDTO) => {
  return axiosInstance.post(`/api/victime/representant`, representantDTO);
};

export const saveOffreIndemnisation = (offreDTO) => {
  return axiosInstance.post(`/api/offre-indemnisation`, offreDTO);
};

export const updateOffreIndemnisation = (offreDTO) => {
  return axiosInstance.put(`/api/offre-indemnisation`, offreDTO);
};

export const updateAyantDroitIndemnisation = (ayantsDroits) => {
  return axiosInstance.put(`/api/victime/ayant-droit-indemnisation`, ayantsDroits);
};

export const saveExpert = (expert) => {
  const payload = { ...expert, fonction: "EXPERT" };
  return axiosInstance.post(`/api/personne`, payload);
};
export const saveExpertise = (expertise) => {
  return axiosMultiPartDataInstance.post(`/api/sinistre/expertise`, expertise, {
    responseType: "blob",
    headers: { "Content-Type": "multipart/form-data" },
  });
};

export const updateExpertise = (expertise) => {
  const payload = JSON.stringify(expertise);
  return axiosInstance.put(`/api/expertise`, payload, {
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const savePriseEnCharge = (pec, id) => {
  return axiosInstance.post(`/api/sinistre/prise-en-charge/codeId/${id}`, pec);
};

export const updatePriseEnCharge = (pec, id) => {
  return axiosInstance.put(
    `/api/sinistre/prise-en-charge/codeId/${id}`,
    JSON.stringify(pec)
  );
};

export const saveDecaissement = (decaissement) => {
  return axiosInstance.post(`/api/decaissement`, decaissement);
};

export const saveDecaissements = (decaissements, grouped = false) => {
  return axiosInstance.post(`/api/decaissement/all?grouped=${grouped}`, decaissements);
};

// Valide en une action toutes les lignes EN_ATTENTE d'un groupe (numéro d'ordre unique).
// dateReglement (YYYY-MM-DD, optionnelle) : antidatage sous habilitation HSI095.
export const validateDecaissementsGroupe = (groupeCodeId, dateReglement) => {
  const query = dateReglement ? `?dateReglement=${dateReglement}` : "";
  return axiosInstance.put(`/api/decaissement/validate-groupe/${groupeCodeId}${query}`);
};

// Rejette en une action toutes les lignes EN_ATTENTE d'un groupe (miroir de la validation groupée).
export const rejectDecaissementsGroupe = (groupeCodeId) => {
  return axiosInstance.put(`/api/decaissement/reject-groupe/${groupeCodeId}`);
};

// Regroupe manuellement des règlements EN_ATTENTE déjà créés (même bénéficiaire) en un règlement groupé.
export const regrouperDecaissements = (codeIds) => {
  return axiosInstance.put(`/api/decaissement/regrouper`, codeIds);
};

export const getDecaissementByCodeId = (codeId) => {
  return axiosInstance.get(`/api/decaissement/codeId/${codeId}`);
};

// Lignes complètes (entités, avec sinistre) d'un règlement groupé — pour les
// documents groupés depuis les listings qui n'ont que des DTO plats.
export const getDecaissementsByGroupe = (groupeCodeId) => {
  return axiosInstance.get(`/api/decaissement/groupe/${groupeCodeId}`);
};

export const updateDecaissement = (decaissement) => {
  return axiosInstance.put(`/api/decaissement`, JSON.stringify(decaissement));
};

export const updateSinistre = (sinistre) => {
  return axiosInstance.put(`/api/sinistre`, sinistre);
};

// Renumérote un sinistre existant (changement de type / nature)
export const renumeroterSinistre = (codeId) => {
  return axiosInstance.put(`/api/sinistre/renumeroter/${codeId}`);
};

export const refuseDossierSinistre = (codeId) => {
  return axiosInstance.get(`/api/sinistre/refus/${codeId}`);
};

export const refreshEtatsSinistres = () => {
  return axiosInstance.get(`/api/sinistre/refresh-etats-sinistres`);
};

export const updateEvaluation = (evaluation) => {
  return axiosInstance.put(`/api/evaluation`, evaluation);
};

export const validateEvaluation = (codeId) => {
  return axiosInstance.get(`/api/evaluation/validate/${codeId}`);
};

export const validateDossierSinistre = (codeId, statutDossier = "O") => {
  const nextStatut = encodeURIComponent(statutDossier);
  return axiosInstance.get(`/api/sinistre/validation/${codeId}/${nextStatut}`);
};

export const checkContratAvailability = async (engins) => {
  return await axiosInstance.post(
    `/api/attestation-digitale/check/availability`,
    engins
  );
};
export const checkAttestationNumber = (numero) => {
  return axiosInstance.get(`/api/lot-attestation/check/${numero}`);
};


export const dispatchLotAttestationDigitale = async (
  dispatchLotAttestation
) => {
  return await axiosInstance.post(
    `/api/attestation-digitale/dispatch`,
    dispatchLotAttestation
  );
};

export const assignNumAttestation = async (engins) => {
  return await axiosInstance.post(`/api/attestation-digitale/assign`, engins);
};

export const validateWithComment = async (validationBonCommandeDTO) => {
  return await axiosInstance.post(
    `/api/bon-commande/validate-comment`,
    validationBonCommandeDTO
  );
};

export const updateBonCommande = async (bonCommande) => {
  return await axiosInstance.put(`/api/bon-commande/`, bonCommande);
};

export const rejectEvaluation = (codeId) => {
  return axiosInstance.get(`/api/evaluation/reject/${codeId}`);
};

export const getEnginByCodeId = (codeId) => {
  return axiosInstance.get(`/api/engin/code/${codeId}`);
};

export const getContratByCodeId = (codeId) => {
  return axiosInstance.get(`/api/services/contrat/${codeId}`);
};

export const getEnginsByPersonneCodeId = (contratCodeId, personneCodeId) => {
  const rep = axiosInstance.get(
    `/api/services/engins/contrat/${contratCodeId}/personne/${personneCodeId}`
  );
  console.log(rep);
  return rep;
};

// dateReglement (YYYY-MM-DD, optionnelle) : antidatage sous habilitation HSI095.
export const validateDecaissement = (codeId, dateReglement) => {
  const query = dateReglement ? `?dateReglement=${dateReglement}` : "";
  return axiosInstance.put(`/api/decaissement/validate/${codeId}${query}`);
};

export const rejectDecaissement = (codeId) => {
  return axiosInstance.put(`/api/decaissement/reject/${codeId}`);
};

// Annulation TECHNIQUE d'un reglement de sinistre (cree la ligne negative + passe l'original en
// REJETE, sans ecriture comptable). Sous habilitation HSI015. Typiquement apres annulation du cheque.
export const annulerReglementTechnique = (codeId, motif) => {
  return axiosInstance.put(`/api/decaissement/annuler-reglement-technique/${codeId}`, { motif });
};

// Annulation TECHNIQUE d'un reglement GROUPE (applique la logique unitaire a chaque ligne du groupe).
// Sous habilitation HSI015. Refuse si un cheque est encore actif (a annuler d'abord).
export const annulerReglementTechniqueGroupe = (groupeReglementCodeId, motif) => {
  return axiosInstance.put(`/api/decaissement/annuler-reglement-technique-groupe/${groupeReglementCodeId}`, { motif });
};

// Modifie UNIQUEMENT le motif d'un reglement valide (sous habilitation HSI089).
export const updateMotifReglement = (codeId, motifReglement) => {
  return axiosInstance.put(`/api/decaissement/motif/${codeId}`, { motifReglement });
};

// Modifie le motif de TOUS les règlements validés d'un groupe (règlement multiple), depuis
// l'ordre de dépense (habilitation HSI089).
export const updateMotifReglementGroupe = (groupeReglementCodeId, motifReglement) => {
  return axiosInstance.put(`/api/decaissement/motif-groupe/${groupeReglementCodeId}`, { motifReglement });
};

// Initialisation d'un decaissement depuis une prise en charge
export const initDecaissementByPriseEnCharge = (pecCodeId, sinistreCodeId) => {
  return axiosInstance.post(
    `/api/decaissement/init/prise-en-charge/${pecCodeId}/${sinistreCodeId}`
  );
};

// Initialisation d'un decaissement depuis une offre d'indemnisation
export const initDecaissementByOffre = (offreCodeId, sinistreCodeId) => {
  return axiosInstance.post(
    `/api/decaissement/init/offre/${offreCodeId}/${sinistreCodeId}`
  );
};

// Assignation d'un cheque a un decaissement (etat VALIDE -> EN_ATTENTE_D_ENCAISSEMENT)
// chequeDTO = { banque, numeroCheque, dateCheque: 'YYYY-MM-DD', montantCheque?, representant? }
export const assignerCheque = (codeId, chequeDTO) => {
  return axiosInstance.put(
    `/api/decaissement/assigner-cheque/${codeId}`,
    JSON.stringify(chequeDTO),
    { headers: { 'Content-Type': 'application/json' } }
  );
};

// Assignation d'un meme cheque a plusieurs decaissements
export const assignerChequeMultiple = (codeIds, numeroCheque, banque, dateCheque) => {
  return axiosInstance.put(
    `/api/decaissement/assigner-cheque-multiple`,
    codeIds,
    {
      params: { numeroCheque, banque, dateCheque },
      headers: { 'Content-Type': 'application/json' }
    }
  );
};

// Remise de cheque (EN_ATTENTE_D_ENCAISSEMENT -> ENCAISSE)
// remiseDTO = { cni, representant?, recepteurCodeId? }
export const remiseCheque = (codeId, remiseDTO) => {
  return axiosInstance.put(
    `/api/decaissement/remise/${codeId}`,
    JSON.stringify(remiseDTO),
    { headers: { 'Content-Type': 'application/json' } }
  );
};

// Annulation d'un cheque (annule tous les decaissements lies)
// decaissementDTO = { codeId, numeroCheque, banque, motifAnnulerCheque, ... }
export const annulerCheque = (decaissementDTO) => {
  return axiosInstance.put(
    `/api/decaissement/annuler-cheque`,
    JSON.stringify(decaissementDTO),
    { headers: { 'Content-Type': 'application/json' } }
  );
};

// Impression (enregistre la date d'impression)
export const printDecaissement = (codeId) => {
  return axiosInstance.put(`/api/decaissement/print/${codeId}`);
};


export const saveSinistreFile = async (file) => {
  if (file) {
    const formData = new FormData();
    formData.append("fichier", file);

    const response = await axiosMultiPartDataInstance.post(
      "/api/sinistre/file",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    console.log(response.data);
    let files = [];
    if (sessionStorage.getItem("file")) {
      files = JSON.parse(sessionStorage.getItem("file"));
    }
    files.push(response.data);
    sessionStorage.setItem("file", JSON.stringify(files));

    return response.data;
  }
};
export const saveReglementCheque = async (file) => {
  if (file) {
    const formData = new FormData();
    formData.append("fichier", file);

    const response = await axiosMultiPartDataInstance.post(
      "/api/reglement/file",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    console.log(response.data);
    sessionStorage.removeItem("file");
    sessionStorage.setItem("file", response.data);

    return response.data;
  }
};

export const saveSinistreFiles = async (file) => {
  if (file) {
    const formData = new FormData();
    formData.append("fichier", file);

    const response = await axiosMultiPartDataInstance.post(
      "/api/sinistre/files",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    console.log(response.data);
    sessionStorage.removeItem("file");
    sessionStorage.setItem("file", response.data);

    return response.data;
  }
};

export const sendSecurePdfEmails = async (files, contrat) => {
  try {
    // Create FormData to handle file upload
    const formData = new FormData();
    files.forEach((file) => {
      formData.append('files', file);
    });

    // Make the POST request using the
    const response = await axiosMultiPartDatainstance.post(
      `/api/attestation-digitale/secure-view-only/${contrat.codeId}`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    console.log("Response:", response.data);
    return response.data;
  } catch (error) {
    console.error(
      "Error:",
      error.response ? error.response.data : error.message
    );
    throw error;
  }
};

export const loadTaxes = (setTaxe) => {
  axiosInstance
    .get(`${API_URL}/api/taxe`)
    .then((res) => {
      console.log("Hey ", res);
      setTaxe(
        res.data.filter((t) => t.activeData && t.libelleTaxe === "TVA")[0]
      ); // Filtre supplémentaire si nécessaire
    })
    .catch((err) => {
      console.error("Erreur lors du chargement des taxes :", err);
      toast.error("Erreur lors du chargement des taxes");
    });
};


const buildAttachmentFormData = (file, meta = {}) => {
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(meta).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      formData.append(key, value);
    }
  });
  return formData;
};

export const uploadSinistrePiece = async (typePiece, file, codeId) => {
  if (!codeId || !file) {
    throw new Error("Paramètres de téléversement de document invalides");
  }

  const formData = buildAttachmentFormData(file, {
    typePiece: typePiece || "DOCUMENT",
    nature: "DOCUMENT",
  });

  const response = await axiosMultiPartDataInstance.post(
    `/api/sinistre/${codeId}/attachments`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};

export const uploadSinistreCapture = async (codeId, file) => {
  if (!codeId || !file) {
    throw new Error("Paramètres de capture invalides");
  }

  const formData = buildAttachmentFormData(file, {
    typePiece: "CAPTURE",
    nature: "CAPTURE",
  });

  const response = await axiosMultiPartDataInstance.post(
    `/api/sinistre/${codeId}/attachments`,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    }
  );

  return response.data;
};
